<template>
  <div class="step-missing-mortgage step-stagger">
    <div class="hero-badge">
      <img class="hero-badge-star" :src="stars.greenLarge" alt="" />
      <img class="hero-badge-icon" :src="icons.assuredWorkload" alt="" />
    </div>

    <h1 class="step-heading" dir="auto">אנחנו כמעט שם!</h1>
    <p class="step-subtitle" dir="auto">
      בחשבון שחיברת לא מצאנו משכנתא פעילה.<br />אפשר לקרוא את המשכנתא בשתי דרכים:
    </p>

    <div v-if="uploadFoundNoMortgage" class="upload-no-result" dir="auto">
      הקובץ נקלט אבל לא הצלחנו לזהות בו משכנתא — אפשר לנסות עם דו"ח אחר או לחבר חשבון.
    </div>

    <div class="divider" />

    <div class="option">
      <p class="option-label" dir="auto">לחבר את חשבון המשכנתא שלכם לרייזאפ</p>
      <CtaButton
        text="לחבר את החשבון"
        :icon="icons.landmark"
        @click="$emit('connect-account')"
      />
    </div>

    <div class="or-divider" dir="auto">
      <span class="or-circle">או</span>
    </div>

    <div class="option">
      <p class="option-label" dir="auto">
        לצרף את דו״ח סיכום היתרות של המשכנתא שלך.
      </p>
      <input
        ref="fileInput"
        type="file"
        accept=".pdf"
        style="display: none;"
        @change="onFileSelected"
      />
      <CtaButton
        text="לצרף את הקובץ"
        :icon="icons.upload"
        :loading="uploadingFile || processing"
        @click="onUploadButtonClick"
      />
      <div v-if="isErrorMessage" class="upload-error" dir="auto" role="alert">
        {{ errorMessage }}
      </div>
    </div>

    <button
      type="button"
      class="help-link"
      dir="auto"
      @click="$emit('help-click')"
    >
      איפה מוצאים את דו״ח המשכנתא?
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MortgageUploadFileMixin from '@/mixins/MortgageUploadFileMixin.vue';
import CtaButton from './CtaButton.vue';

/* eslint-disable global-require */
const icons = {
  assuredWorkload: require('../assets/lucide-assured-workload.svg'),
  landmark: require('../assets/lucide-landmark.svg'),
  upload: require('../assets/lucide-upload.svg'),
};
const stars = {
  greenLarge: require('../assets/star-union-green.svg'),
};
/* eslint-enable global-require */

export default {
  name: 'StepMissingMortgage',
  mixins: [MortgageUploadFileMixin],
  components: { CtaButton },
  props: {
    uploadFoundNoMortgage: {
      type: Boolean,
      default: false,
    },
    processing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons,
      stars,
      uploadingFile: false,
      isErrorMessage: false,
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters('otsState', ['isInOtsFlow']),
    pageName() {
      return 'MortgageRefinanceConnect_MissingMortgage';
    },
  },
  methods: {
    onUploadButtonClick() {
      this.$refs.fileInput.click();
    },
    // Override the mixin default — let the parent re-check mortgage data
    // and route to the right page instead of hard-refreshing.
    onUploadSuccess() {
      this.$emit('upload-success');
    },
  },
};
</script>

<style lang="scss" scoped>
.step-missing-mortgage {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 27px 24px;
}

.upload-error {
  font-family: 'SimplerPro', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #b4438e;
  background: #FEEEF1;
  border-radius: 12px;
  padding: 10px 16px;
  margin: 12px 0 0;
  text-align: center;
}

.upload-no-result {
  font-family: 'SimplerPro', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #205441;
  background: #FFEFC3;
  border-radius: 12px;
  padding: 12px 16px;
  margin: 0 0 16px;
  text-align: center;
}

.hero-badge {
  position: relative;
  width: 70px;
  height: 70px;
  margin: 16px auto 24px;
}

.hero-badge-star {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.hero-badge-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  display: block;
  // White icon on the green star
  filter: brightness(0) invert(1);
}

.step-heading {
  font-family: 'SimplerProAlte', sans-serif;
  font-size: 32px;
  line-height: 36px;
  font-weight: 900;
  font-style: normal;
  font-synthesis: none;
  color: #205441;
  text-align: center;
  margin: 0 auto 20px;
  max-width: 300px;

  @media (max-width: 500px) {
    font-size: 26px;
    line-height: 32px;
    margin-bottom: 16px;
  }
}

.step-subtitle {
  font-family: 'SimplerPro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #202125;
  text-align: center;
  margin: 0 auto 32px;
  max-width: 320px;
  text-wrap: balance;
}

.divider {
  height: 1px;
  background: rgba(32, 33, 37, 0.15);
  margin: 0 auto 32px;
  width: 100%;
}

.option {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.option-label {
  font-family: 'SimplerPro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #202125;
  text-align: center;
  // Pull 6px outside the step's horizontal padding so the second option
  // label ("לצרף את דו״ח סיכום היתרות של המשכנתא שלך.") fits on one line
  // at 375px phone width — natural text width is 307px but the container
  // gives it 299px.
  margin: 0 -6px 16px;
  max-width: none;
  text-wrap: balance;
}

.or-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px auto;
}

.or-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #dce8f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'SimplerPro', sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #202125;
}

.help-link {
  background: none;
  border: 0;
  padding: 0;
  margin: 16px auto 0;
  font-family: 'SimplerPro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #5d7afd;
  text-decoration: underline;
  text-decoration-solid: solid;
  cursor: pointer;
}
</style>
